<template>
  <div
    class="content-media"
    ref="contentMediaRef"
    style="padding-inline: unset"
  >
    <VideoJSPlayer
      v-if="data.content?.asset?.mimeType.includes('video')"
      class="video"
      :class="{ 'video-ready': videoReady }"
      :options="{
        autoplay: true,
        muted: true,
        controls: false,
        loop: true,
        auto: true,
        fill: true,
        responsive: true,
        playsinline: true,
        hls: {
          limitRenditionByPlayerDimensions: true,
          useDevicePixelRatio: true,
        },
        bandwidth: 10e6,
        sources: [
          {
            src: videoSource,
            type: 'application/x-mpegURL',
          },
        ],
      }"
      @player-ready="onVideoReady"
    />
    <div
      ref="imageRef"
      class="image"
      v-if="data.content?.asset?.mimeType.includes('image')"
    >
      <img :src="data.content.asset.url" alt="" />
    </div>
    <h1 v-if="data.title" class="title text-2xl">{{ data.title }}</h1>
  </div>
</template>

<script setup lang="ts">
import type { ContentMediaFragment } from '#gql';
import VideoJSPlayer from '~/components/partials/VideoJSPlayer.vue';
import { useHeaderIntersection } from '~/composables/useHeaderIntersection';

const props = defineProps<{
  data: ContentMediaFragment;
}>();

let videoReady = ref(false);

// Check if the content is intersecting with the header
const contentMediaRef = ref<HTMLElement | null>(null);
const imageRef = ref<HTMLElement | null>(null);
// The computed is needed because expose with option API is not working
const triggerElement = computed<HTMLElement | null>(
  () => contentMediaRef.value?.querySelector('.video') || null,
);

useHeaderIntersection(triggerElement);
useHeaderIntersection(imageRef);

const videoSource = computed(() => {
  if (!props.data.content?.asset?.mimeType.includes('video')) return '';
  const asset = props.data.content.asset as {
    video?: { streamingUrl: string };
  };
  return asset.video?.streamingUrl ?? '';
});

function onVideoReady() {
  videoReady.value = true;
}
</script>

<style scoped lang="scss">
.content-media {
  width: 100%;
  min-height: 100%;
  height: auto;
  padding: unset;
  position: relative;

  .image,
  .video {
    aspect-ratio: 15/9;
    height: 100%;
    width: 100%;
  }

  .image {
    object-fit: cover;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .video {
    display: none;
    &.video-ready {
      display: block;
    }
  }

  .title {
    position: absolute;
    color: white;
    bottom: calc(var(--base-component-padding-block) + 5rem);
    left: 3.5rem;
    max-width: 58%;
  }
}
</style>
